/**
 * AircraftMutateDrawer - Sheet/Dialog wrapper for aircraft create/edit
 *
 * Handles: chrome (Sheet or Dialog), data fetching, dirty-state discard dialog.
 * Toggle button lets user switch between side-panel and modal mode.
 * Form fields are rendered by AircraftForm.
 */

import { useState, useMemo } from 'react'
import { PanelRight, Maximize2, X } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAircraftById } from '@/hooks/useAircraft'
import { AircraftForm } from './AircraftForm'
import type { AircraftFormValues } from './aircraft-form-schema'

type DrawerMode = 'sidePanel' | 'modal'

interface AircraftMutateDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  aircraftId?: string
}

export function AircraftMutateDrawer({
  open,
  onOpenChange,
  aircraftId,
}: AircraftMutateDrawerProps) {
  const isEdit = !!aircraftId

  // State
  const [isDirty, setIsDirty] = useState(false)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('sidePanel')

  // Fetch existing aircraft for edit mode
  const { data: aircraft, isLoading: isLoadingAircraft } = useAircraftById(
    isEdit ? aircraftId : undefined
  )

  // Memoize initialData to prevent form reset on every parent re-render
  const initialData = useMemo<Partial<AircraftFormValues> | undefined>(() => {
    if (!aircraft) return undefined
    return {
      tail_number: aircraft.tail_number,
      aircraft_type_id: aircraft.aircraft_type_id,
      manufacturer: aircraft.manufacturer,
      model: aircraft.model,
      owner_client_id: aircraft.owner_client_id,
      operator_client_id: aircraft.operator_client_id,
      owner_name: aircraft.owner_name,
      length: aircraft.length,
      wingspan: aircraft.wingspan,
      height: aircraft.height,
      tail_height: aircraft.tail_height,
      empty_weight: aircraft.empty_weight,
      max_takeoff_weight: aircraft.max_takeoff_weight,
      registration_country: aircraft.registration_country,
      serial_number: aircraft.serial_number,
      year_manufactured: aircraft.year_manufactured,
      annual_inspection_due: aircraft.annual_inspection_due,
      insurance_expiry: aircraft.insurance_expiry,
      home_location_id: aircraft.home_location_id,
      current_location_id: aircraft.current_location_id,
      current_area_id: aircraft.current_area_id,
      custom_model_file_path: aircraft.custom_model_file_path,
      custom_model_scale: aircraft.custom_model_scale,
      status: aircraft.status,
      notes: aircraft.notes,
    }
  }, [aircraft])

  // Handle close with dirty check
  const handleClose = () => {
    if (isDirty) {
      setShowDiscardDialog(true)
    } else {
      onOpenChange(false)
    }
  }

  const handleDiscard = () => {
    setShowDiscardDialog(false)
    setIsDirty(false)
    onOpenChange(false)
  }

  // Handle successful save
  const handleSuccess = () => {
    setIsDirty(false)
    onOpenChange(false)
  }

  const isLoading = isEdit && isLoadingAircraft

  const title = isEdit ? `Edit Aircraft: ${aircraft?.tail_number || '...'}` : 'Add Aircraft'
  const description = isEdit
    ? 'Update aircraft details below.'
    : 'Add a new aircraft to track.'
  const submitLabel = isEdit ? 'Update' : 'Create'

  // --- Shared elements ---

  const headerActions = (
    <div className="flex items-center gap-0.5">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setDrawerMode(drawerMode === 'sidePanel' ? 'modal' : 'sidePanel')}
          >
            {drawerMode === 'sidePanel' ? (
              <Maximize2 className="w-4 h-4" />
            ) : (
              <PanelRight className="w-4 h-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {drawerMode === 'sidePanel' ? 'Switch to modal' : 'Switch to side panel'}
        </TooltipContent>
      </Tooltip>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 opacity-70 hover:opacity-100"
        onClick={handleClose}
      >
        <X className="w-4 h-4" />
        <span className="sr-only">Close</span>
      </Button>
    </div>
  )

  const formElement = isLoading ? (
    <div className="flex items-center justify-center py-8 text-muted-foreground">
      Loading aircraft data...
    </div>
  ) : (
    <AircraftForm
      variant="full"
      aircraftId={aircraftId}
      initialData={initialData}
      onSuccess={handleSuccess}
      onDirtyChange={setIsDirty}
      formId="aircraft-drawer-form"
      hideButtons
    />
  )

  const footerButtons = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={handleClose}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="aircraft-drawer-form"
        disabled={isLoading}
      >
        {submitLabel}
      </Button>
    </>
  )

  const discardDialog = (
    <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. Are you sure you want to close? Your
            changes will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={handleDiscard}>
            Discard
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  // --- Modal mode ---
  if (drawerMode === 'modal') {
    return (
      <>
        <Dialog open={open} onOpenChange={handleClose}>
          <DialogContent showCloseButton={false} className="flex flex-col sm:max-w-[700px] max-h-[90vh] p-0 overflow-hidden">
            <DialogHeader className="px-6 py-4 border-b shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>{title}</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
                </div>
                {headerActions}
              </div>
            </DialogHeader>

            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="px-6 py-4">
                {formElement}
              </div>
            </div>

            <DialogFooter className="flex-row justify-end gap-2 px-6 py-4 border-t bg-background shrink-0">
              {footerButtons}
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {discardDialog}
      </>
    )
  }

  // --- Side panel mode (default) ---
  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          showCloseButton={false}
          className="flex flex-col w-full sm:max-w-2xl p-0 overflow-hidden"
          onInteractOutside={(e) => {
            if (isDirty) e.preventDefault()
          }}
          onEscapeKeyDown={(e) => {
            if (isDirty) {
              e.preventDefault()
              setShowDiscardDialog(true)
            }
          }}
        >
          <SheetHeader className="px-4 py-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle>{title}</SheetTitle>
                <SheetDescription>{description}</SheetDescription>
              </div>
              {headerActions}
            </div>
          </SheetHeader>

          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="px-4 py-4">
              {formElement}
            </div>
          </div>

          <SheetFooter className="flex-row justify-end gap-2 px-4 py-4 border-t bg-background shrink-0">
            {footerButtons}
          </SheetFooter>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  )
}
